import { CheckCircle, Clock, Mail } from "lucide-react";
import { motion } from "framer-motion";

interface WorkflowStep {
  label: string;
  status: "completed" | "in-progress" | "pending";
  timestamp?: string;
  notification?: boolean;
}

interface WorkflowIndicatorProps {
  steps: WorkflowStep[];
  title: string;
}

export default function WorkflowIndicator({
  steps,
  title,
}: WorkflowIndicatorProps) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "text-green-400 bg-green-500/10 border-green-500/30";
      case "in-progress":
        return "text-blue-400 bg-blue-500/10 border-blue-500/30";
      case "pending":
        return "text-gray-400 bg-gray-500/10 border-gray-500/30";
      default:
        return "text-gray-400 bg-gray-500/10 border-gray-500/30";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="w-4 h-4" />;
      case "in-progress":
        return <Clock className="w-4 h-4 animate-pulse" />;
      case "pending":
        return <Clock className="w-4 h-4 opacity-50" />;
      default:
        return <Clock className="w-4 h-4 opacity-50" />;
    }
  };

  return (
    <div className="bg-card border border-border/50 rounded-xl p-4 sm:p-6 shadow-lg">
      <h2 className="text-lg font-bold text-foreground mb-4 font-serif flex items-center gap-2">
        <Clock className="w-5 h-5 text-primary" />
        {title}
      </h2>

      <div className="space-y-3">
        {steps.map((step, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`flex items-start gap-3 p-3 rounded-lg border ${getStatusColor(
              step.status,
            )}`}
          >
            <div className="mt-0.5">{getStatusIcon(step.status)}</div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">{step.label}</p>
                {step.notification && (
                  <div className="flex items-center gap-1 text-xs opacity-75">
                    <Mail className="w-3 h-3" />
                    <span>Email sent</span>
                  </div>
                )}
              </div>
              {step.timestamp && (
                <p className="text-xs opacity-60 mt-0.5">
                  {new Date(step.timestamp).toLocaleString()}
                </p>
              )}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
